// src/pages/languages/AddMCQToLanguage.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Hash,
  AlignLeft,
  Save,
  ArrowRight,
  Loader2,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import {
  languageVocabularyAPI,
  languageGrammarAPI,
} from "../../services/languageService";
import TranslationField from "../../components/TranslationField";
import ImageField from "../../components/ImageField";
import { withFiles } from "../../utils/withFiles";

const emptyForm = {
  question_text: "",
  options: ["", "", "", ""],
  correct_answer: "",
  explanation: "",
  english_explanation: "",
  translation: "",
  difficulty: "MEDIUM",
  points: 1,
};

export default function AddMCQToLanguage({ questionType = "VOCABULARY" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { skillId, questionId } = useParams(); // ← questionId هيبقى موجود بس في وضع الـ Edit
  const isEditMode = Boolean(questionId);

  const isVocabulary = questionType === "VOCABULARY";
  const api = isVocabulary ? languageVocabularyAPI : languageGrammarAPI;

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionQuestions, setSessionQuestions] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [existingImage, setExistingImage] = useState("");

  // في وضع الـ Edit: هات بيانات السؤال من الـ state اللي بعتناه من LanguageSkillDetails
 useEffect(() => {
  if (!isEditMode) return;

  const passedQuestion = location.state?.question;
  if (!passedQuestion) {
    setNotFound(true);
    return;
  }

  // الباك اند بيرجع choice_a..choice_d بدل options array، بنجمعهم هنا
  const rawOptions = [
    passedQuestion.choice_a,
    passedQuestion.choice_b,
    passedQuestion.choice_c,
    passedQuestion.choice_d,
  ].filter((v) => v !== null && v !== undefined);

  // نكمل لحد 4 خانات فاضية لو أقل من كده
  const paddedOptions = [...rawOptions];
  while (paddedOptions.length < 4) paddedOptions.push("");

  setForm({
    question_text: passedQuestion.question_text || "",
    options: paddedOptions,
    correct_answer: passedQuestion.correct_answer || "",
    explanation: passedQuestion.explanation || "",
    english_explanation: passedQuestion.english_explanation || "",
    translation: passedQuestion.translation || "",
    difficulty: passedQuestion.difficulty || "MEDIUM",
    points: passedQuestion.points ?? 1,
  });
  setExistingImage(passedQuestion.question_image || "");
}, [isEditMode, location.state]);

  const updateOption = (index, value) => {
    const updated = [...form.options];
    updated[index] = value;
    setForm({ ...form, options: updated });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setImageFile(null);
  };

  const validate = () => {
    if (!form.question_text.trim()) return "نص السؤال مطلوب";
    const filled = form.options.filter((o) => o.trim());
    if (filled.length < 2) return "يجب إضافة خيارين على الأقل";
    if (!form.correct_answer.trim()) return "الإجابة الصحيحة مطلوبة";
    if (!form.options.includes(form.correct_answer))
      return "الإجابة الصحيحة يجب أن تكون أحد الخيارات";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      question_text: form.question_text,
      options: form.options.filter((o) => o.trim()),
      correct_answer: form.correct_answer,
      explanation: form.explanation,
      english_explanation: form.english_explanation,
      translation: form.translation,
      difficulty: form.difficulty,
      points: form.points,
      language_skill: parseInt(skillId),
    };

    try {
      setLoading(true);
      setError("");

      if (isEditMode) {
        // ── وضع التعديل: تحديث السؤال ثم رجوع لصفحة تفاصيل المهارة ──
        await api.update(questionId, withFiles(payload, { question_image: imageFile }));
        navigate(`/dashboard/languages/skills/${skillId}`, {
          state: { flash: "تم تعديل السؤال بنجاح" },
        });
        return;
      }

      // ── وضع الإنشاء: زي ما كان بالظبط ──
      const res = await api.create(withFiles(payload, { question_image: imageFile }));
      setSessionQuestions((prev) => [
        {
          id: res.question.id,
          question_text: form.question_text,
          correct_answer: form.correct_answer,
        },
        ...prev,
      ]);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || "حدث خطأ، حاول مرة أخرى");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFromSession = async (id) => {
    if (!window.confirm("متأكد إنك عايز تحذف السؤال ده؟")) return;
    try {
      await api.delete(id);
      setSessionQuestions((prev) => prev.filter((q) => q.id !== id));
    } catch (err) {
      alert("فشل حذف السؤال");
    }
  };

  const Icon = isVocabulary ? Hash : AlignLeft;
  const label = isVocabulary ? "Vocabulary" : "Grammar";

  // لو المستخدم فتح لينك الـ edit مباشرة من غير state (refresh أو مشاركة لينك)
  if (isEditMode && notFound) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 text-center py-16">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
        <p className="text-gray-600">
          محتاج تفتح صفحة التعديل من زرار "تعديل" جوه صفحة تفاصيل المهارة، مش مباشرة باللينك.
        </p>
        <button
          onClick={() => navigate(`/dashboard/languages/skills/${skillId}`)}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium"
        >
          الرجوع لصفحة المهارة
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/dashboard/languages/skills/${skillId}`)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
          <Icon className="w-5 h-5 text-blue-700" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">
          {isEditMode ? `تعديل سؤال ${label}` : `إضافة سؤال ${label}`}
        </h1>
        {!isEditMode && sessionQuestions.length > 0 && (
          <span className="mr-auto bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1 rounded-full">
            {sessionQuestions.length} سؤال أُضيف
          </span>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-left">
            نص السؤال <span className="text-red-500">*</span>
          </label>
          <textarea
            value={form.question_text}
            onChange={(e) => setForm({ ...form, question_text: e.target.value })}
            rows={3}
            placeholder="اكتب نص السؤال..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-left resize-none"
          />
          <TranslationField
            label="ترجمة نص السؤال"
            value={form.translation}
            onChange={(v) => ((e) => setForm({ ...form, translation: e.target.value }))({ target: { value: v } })}
          />
          <ImageField
            label="صورة السؤال (اختياري)"
            file={imageFile}
            existingUrl={existingImage}
            onChange={setImageFile}
          />
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 text-left">
            الخيارات <span className="text-red-500">*</span>
          </label>
          {form.options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="radio"
                name="correct"
                checked={form.correct_answer === opt && opt.trim() !== ""}
                onChange={() => opt.trim() && setForm({ ...form, correct_answer: opt })}
                className="accent-blue-600"
              />
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">
                  {String.fromCharCode(65 + i)}
                </span>
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const prevVal = form.options[i];
                    updateOption(i, e.target.value);
                    if (form.correct_answer === prevVal)
                      setForm((prev) => ({ ...prev, correct_answer: e.target.value }));
                  }}
                  placeholder={`الخيار ${String.fromCharCode(65 + i)}`}
                  className={`w-full pl-8 pr-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-left ${
                    form.correct_answer === opt && opt.trim()
                      ? "border-blue-400 bg-blue-50"
                      : "border-gray-200"
                  }`}
                />
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-400 text-left">
            اختر الـ Radio button بجانب الإجابة الصحيحة
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-left">
            الشرح بالعربي (اختياري)
          </label>
          <textarea
            value={form.explanation}
            onChange={(e) => setForm({ ...form, explanation: e.target.value })}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-left resize-none"
          />
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-left">
            Explanation in English (optional)
          </label>
          <textarea
            value={form.english_explanation}
            onChange={(e) => setForm({ ...form, english_explanation: e.target.value })}
            rows={2}
            dir="rtl"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-left resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-left">الصعوبة</label>
            <select
              value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-left bg-white"
            >
              <option value="EASY">سهل</option>
              <option value="MEDIUM">متوسط</option>
              <option value="HARD">صعب</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-left">النقاط</label>
            <input
              type="number"
              value={form.points}
              onChange={(e) => setForm({ ...form, points: parseInt(e.target.value) || 1 })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(`/dashboard/languages/skills/${skillId}`)}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
          >
            {isEditMode ? "إلغاء" : "إنهاء والرجوع للمهارة"}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isEditMode ? (
              <Save className="w-4 h-4" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {isEditMode ? "حفظ التعديلات" : "حفظ وإضافة سؤال آخر"}
          </button>
        </div>
      </form>

      {!isEditMode && sessionQuestions.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="font-bold text-gray-900 mb-3 text-left">
            الأسئلة المُضافة ({sessionQuestions.length})
          </h3>
          <div className="space-y-2">
            {sessionQuestions.map((q) => (
              <div
                key={q.id}
                className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl px-4 py-3"
              >
                <button
                  onClick={() => handleDeleteFromSession(q.id)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="flex-1 text-left">
                  <p className="text-sm text-gray-800 line-clamp-1">{q.question_text}</p>
                  <p className="text-xs text-gray-400">الإجابة الصحيحة: {q.correct_answer}</p>
                </div>
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}